// 사용자 앱 하단 탭바.
// 작업계획서 4.3 (13번): 메인 / 카테고리 / 장바구니 / 마이페이지.
// 디자인 토큰 확정 전이므로 텍스트 + 강조 색만으로 구분한다 (이모지 금지 - 아이콘셋 도입은 Phase 2).

import { NavLink, useLocation } from "react-router-dom";
import { Home, LayoutGrid, ShoppingCart, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCart } from "@/features/cart/useCart.ts";

interface Tab {
  to: string;
  label: string;
  // 탭 아이콘. 라벨과 함께 노출하므로 아이콘 자체는 보조기기에서 숨긴다(aria-hidden).
  icon: LucideIcon;
}

const tabs: ReadonlyArray<Tab> = [
  { to: "/", label: "메인", icon: Home },
  { to: "/category", label: "카테고리", icon: LayoutGrid },
  { to: "/cart", label: "장바구니", icon: ShoppingCart },
  { to: "/me", label: "마이페이지", icon: User },
];

// 하단 고정 탭바.
// 큰 글씨/넉넉한 터치 영역(작업계획서 §10) 을 기본값으로 적용한다.
export default function BottomTabBar() {
  const cart = useCart();
  const cartCount = cart.count;
  // 탭 간 이동은 히스토리를 얕게 유지한다(이미 비-메인 탭에 있으면 replace).
  // 그래야 어느 탭에서든 시스템 뒤로가기가 메인으로 가고, 탭들이 히스토리에 쌓이지 않는다.
  const location = useLocation();
  const onNonHomeTab = location.pathname === "/category" || location.pathname === "/cart" || location.pathname === "/me";
  // pb-[--safe-bottom]: 제스처 내비게이션 바 안전영역만큼 바닥 여백을 늘려 탭이 시스템 바에 겹치지 않게 한다(웹은 0).
  return (
    <nav
      aria-label="하단 메뉴"
      className="fixed inset-x-0 bottom-0 z-10 grid grid-cols-4 border-t border-ink-200 bg-surface pb-[var(--safe-bottom)] shadow-bar"
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === "/"}
            replace={onNonHomeTab}
            className={({ isActive }) =>
              [
                "relative flex h-14 flex-col items-center justify-center gap-0.5 text-xs transition-colors duration-fast ease-standard",
                // 활성 탭은 브랜드색 + 굵게로 명확히 구분(중장년 가독성)
                isActive ? "font-bold text-brand-600" : "text-ink-600",
              ].join(" ")
            }
          >
            {/* 아이콘은 라벨과 중복이므로 보조기기에서 숨김 */}
            <Icon size={22} aria-hidden="true" />
            {tab.label}
            {/* 장바구니 탭에만 항목 수 배지 표시. 0 이면 숨김. */}
            {tab.to === "/cart" && cartCount > 0 ? (
              <span
                aria-label={`담긴 상품 ${cartCount}종`}
                className="absolute right-[22%] top-1.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[11px] font-bold text-white"
              >
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            ) : null}
          </NavLink>
        );
      })}
    </nav>
  );
}
